import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import MaturaFunctionGraph from "../../../../components/GraphPlotter/MaturaFunctionGraph";
import MaturaPiecewiseInequalityExplanationGraph from "./MaturaPiecewiseInequalityExplanationGraph";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


import {
  randInt,
  parseSingleInterval,
  parseIntervalsUnion,
  intervalHasInvalidOrder,
  getIntervalRaw,
  intervalObjectToLatex,
  intervalsEqualList,
  formatLinear,
  sortIntervalsByLeft,
} from "./helpers/MaturaPiecewiseFunctionUtils";

/* ========================================================================== */
/* POMOCNICZE                                                                 */
/* ========================================================================== */

const TOL = 1e-9;

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function mirrorIntervalY(I) {
  if (!I) return null;
  return {
    left: -I.right,
    right: -I.left,
    leftClosed: I.rightClosed,
    rightClosed: I.leftClosed,
  };
}

function intervalsToLatexUnion(intervals) {
  if (!intervals || !intervals.length) return "";
  return intervals.map((I) => intervalObjectToLatex(I)).join(" \\cup ");
}

// zakres wartości jednego kawałka liniowego na przedziale I
function pieceRange(a, b, I) {
  if (!I) return null;

  if (Math.abs(a) < TOL) {
    // funkcja stała
    return {
      left: b,
      right: b,
      leftClosed: true,
      rightClosed: true,
    };
  }

  const yL = a * I.left + b;
  const yR = a * I.right + b;

  if (Math.abs(yL - yR) < TOL) {
    return {
      left: yL,
      right: yL,
      leftClosed: true,
      rightClosed: true,
    };
  }

  const increasing = yL < yR;
  const low = increasing ? yL : yR;
  const high = increasing ? yR : yL;
  const lowClosed = increasing ? I.leftClosed : I.rightClosed;
  const highClosed = increasing ? I.rightClosed : I.leftClosed;

  return {
    left: low,
    right: high,
    leftClosed: lowClosed,
    rightClosed: highClosed,
  };
}

// liczenie zbioru wartości z trzech kawałków po odbiciach
function computeRangeFromData(data) {
  const pieces = [
    pieceRange(data.a1, data.b1, data.interval1),
    pieceRange(data.a2, data.b2, data.interval2),
    pieceRange(data.a3, data.b3, data.interval3),
  ].filter(Boolean);

  if (!pieces.length) {
    return { intervals: [], hull: null };
  }

  const sorted = pieces.sort((A, B) => {
    if (A.left !== B.left) return A.left - B.left;
    return A.right - B.right;
  });

  const merged = [];
  let cur = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const nxt = sorted[i];

    const separated =
      nxt.left > cur.right + TOL ||
      (Math.abs(nxt.left - cur.right) < TOL &&
        !cur.rightClosed &&
        !nxt.leftClosed);

    if (separated) {
      merged.push(cur);
      cur = { ...nxt };
    } else {
      // zakresy stykają się lub nachodzą – łączymy

      // jeśli zaczynają się w tej samej wartości – domknięcie lewej
      if (Math.abs(nxt.left - cur.left) < TOL) {
        cur.leftClosed = cur.leftClosed || nxt.leftClosed;
      }

      // prawa granica
      if (nxt.right > cur.right + TOL) {
        cur.right = nxt.right;
        cur.rightClosed = nxt.rightClosed;
      } else if (Math.abs(nxt.right - cur.right) < TOL) {
        cur.rightClosed = cur.rightClosed || nxt.rightClosed;
      }
    }
  }
  merged.push(cur);

  const hull = {
    left: merged[0].left,
    right: merged[merged.length - 1].right,
    leftClosed: merged[0].leftClosed,
    rightClosed: merged[merged.length - 1].rightClosed,
  };

  return { intervals: merged, hull };
}

// wartość f(x) na bazie danych funkcji (po odbiciu)
function valueOfF(data, x) {
  const { a1, b1, a2, b2, a3, b3, interval1, interval2, interval3 } = data;

  const inInterval = (I) => {
    if (!I) return false;
    if (x > I.left + TOL && x < I.right - TOL) return true;
    if (Math.abs(x - I.left) < TOL && I.leftClosed) return true;
    if (Math.abs(x - I.right) < TOL && I.rightClosed) return true;
    return false;
  };

  if (inInterval(interval1)) return a1 * x + b1;
  if (inInterval(interval2)) return a2 * x + b2;
  if (inInterval(interval3)) return a3 * x + b3;

  // awaryjnie (nie powinno się zdarzyć)
  return a1 * x + b1;
}

/* ========================================================================== */
/* ODBICIA                                                                    */
/* ========================================================================== */
function buildPointsFromData(data) {
  const { a1, b1, a2, b2, a3, b3, interval1, interval2, interval3 } = data;

  const f1 = (x) => a1 * x + b1;
  const f2 = (x) => a2 * x + b2;
  const f3 = (x) => a3 * x + b3;

  const x1 = interval1.left;
  const x2 = interval1.right; // = interval2.left
  const x3 = interval2.right; // = interval3.left
  const x4 = interval3.right;

  const eps = 0;

  const x1Line = x1 + eps;
  const x2LineRight = x2 + eps;
  const x3LineLeft = x3 - eps;
  const x3LineRight = x3 + eps;
  const x4Line = x4 - eps;

  return {
    A: {
      x: x1,
      y: f1(x1),
      markerSymbol: interval1.leftClosed ? "circle-filled" : "circle-open",
      showLabel: false,
    },
    AL: {
      x: x1Line,
      y: f1(x1Line),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },

    // w tej wersji B nie ma markera (tak jak było), ale MUSI mieć poprawne y
    B: {
      x: x2,
      y: f2(x2),
      showDot: false,
      showLabel: false,
    },

    Cline: {
      x: x2LineRight,
      y: f2(x2LineRight),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },
    Dline: {
      x: x3LineLeft,
      y: f2(x3LineLeft),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },

    // D – koniec środkowego kawałka, marker wg interval2.rightClosed
    D: {
      x: x3,
      y: f2(x3),
      markerSymbol: interval2.rightClosed ? "circle-filled" : "circle-open",
      showLabel: false,
    },

    // E – początek prawego kawałka, marker wg interval3.leftClosed
    E: {
      x: x3,
      y: f3(x3),
      markerSymbol: interval3.leftClosed ? "circle-filled" : "circle-open",
      showLabel: false,
    },

    Eline: {
      x: x3LineRight,
      y: f3(x3LineRight),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },
    Fline: {
      x: x4Line,
      y: f3(x4Line),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },

    F: {
      x: x4,
      y: f3(x4),
      markerSymbol: interval3.rightClosed ? "circle-filled" : "circle-open",
      showLabel: false,
    },
  };
}

// Odbicie względem osi OX: g(x) = -f(x)
function applyFlipOX(baseData) {
  const dataX = deepClone(baseData);

  // g(x) = -f(x)
  dataX.a1 = -baseData.a1;
  dataX.b1 = -baseData.b1;
  dataX.a2 = -baseData.a2;
  dataX.b2 = -baseData.b2;
  dataX.a3 = -baseData.a3;
  dataX.b3 = -baseData.b3;

  // dziedzina bez zmian
  dataX.domainInterval = deepClone(baseData.domainInterval);

  // zakres wartości przy OX – wstępnie (potem i tak przeliczysz computeRangeFromData)
  const R = baseData.rangeInterval;
  dataX.rangeInterval = {
    left: -R.right,
    right: -R.left,
    leftClosed: R.rightClosed,
    rightClosed: R.leftClosed,
  };

  // miejsca zerowe bez zmian (x te same)
  dataX.zerosInterval = deepClone(baseData.zerosInterval);

  // punkt odniesienia ten sam
  dataX.ineqRefX = baseData.ineqRefX;

  // nierówność: < -> >
  dataX.ineqSolution = deepClone(baseData.ineqSolution);
  dataX.ineqRelation = ">";

  // ✅ KLUCZ: punkty liczymy z NOWYCH współczynników/przedziałów
  dataX.points = buildPointsFromData(dataX);

  return dataX;
}

// Odbicie względem osi OY: g(x) = f(-x)
function applyFlipOY(baseData) {
  const dataY = deepClone(baseData);

  // przedziały: I1' = odbicie I3, I2' = odbicie I2, I3' = odbicie I1
  dataY.interval1 = mirrorIntervalY(baseData.interval3);
  dataY.interval2 = mirrorIntervalY(baseData.interval2);
  dataY.interval3 = mirrorIntervalY(baseData.interval1);

  // współczynniki: g(x) = f(-x)
  dataY.a1 = -baseData.a3;
  dataY.b1 = baseData.b3;

  dataY.a2 = -baseData.a2;
  dataY.b2 = baseData.b2;

  dataY.a3 = -baseData.a1;
  dataY.b3 = baseData.b1;

  // dziedzina: odbicie
  dataY.domainInterval = mirrorIntervalY(baseData.domainInterval);

  // range przy OY bez zmian (i tak liczysz computeRangeFromData)
  dataY.rangeInterval = deepClone(baseData.rangeInterval);

  // miejsca zerowe = odbicie
  dataY.zerosInterval = mirrorIntervalY(baseData.zerosInterval);

  // punkt odniesienia i rozwiązanie nierówności – odbicie
  dataY.ineqRefX = -baseData.ineqRefX;
  if (Array.isArray(baseData.ineqSolution)) {
    dataY.ineqSolution = baseData.ineqSolution.map((I) => mirrorIntervalY(I));
  } else if (baseData.ineqSolution) {
    dataY.ineqSolution = [mirrorIntervalY(baseData.ineqSolution)];
  } else {
    dataY.ineqSolution = [];
  }

  dataY.ineqRelation = baseData.ineqRelation || "<";

  // ✅ KLUCZ: punkty liczymy z NOWYCH współczynników/przedziałów
  dataY.points = buildPointsFromData(dataY);

  return dataY;
}

/* ========================================================================== */
/* GENERATOR ZADANIA                                                          */
/* ========================================================================== */

function createJune2025OriginalTask() {
  return {
    type: "MaturaPiecewiseFunctionIntervalsOpenTask",
    data: {
      a1: -1,
      b1: -2,
      a2: 0,
      b2: 0,
      a3: 1,
      b3: 0,
      interval1: { left: -5, right: -2, leftClosed: false, rightClosed: true },
      interval2: { left: -2, right: 1, leftClosed: false, rightClosed: true },
      interval3: { left: 1, right: 3, leftClosed: false, rightClosed: true },
      domainInterval: { left: -5, right: 3, leftClosed: false, rightClosed: true },
      rangeInterval: { left: 0, right: 3, leftClosed: true, rightClosed: true },
      rangeIntervals: [
        { left: 0, right: 3, leftClosed: true, rightClosed: true },
      ],
      zerosInterval: { left: -2, right: 1, leftClosed: true, rightClosed: true },
      ineqRefX: -3,
      ineqRelation: "<",
      ineqSolution: [
        { left: -3, right: 1, leftClosed: false, rightClosed: true },
      ],
      points: {
        A: { x: -5, y: 3, markerSymbol: "circle-open", showLabel: false },
        AL: { x: -5, y: 3, showDot: false, showMarker: false, showLabel: false, label: "" },
        B: { x: -2, y: 0, showDot: false, showLabel: false },
        Cline: { x: -1.85, y: 0, showDot: false, showMarker: false, showLabel: false, label: "" },
        Dline: { x: 1, y: 0, showDot: false, showMarker: false, showLabel: false, label: "" },
        D: { x: 1, y: 0, markerSymbol: "circle-filled", showLabel: false },
        E: { x: 1, y: 1, markerSymbol: "circle-open", showLabel: false },
        Eline: { x: 1, y: 1, showDot: false, showMarker: false, showLabel: false, label: "" },
        Fline: { x: 3, y: 3, showDot: false, showMarker: false, showLabel: false, label: "" },
        F: { x: 3, y: 3, markerSymbol: "circle-filled", showLabel: false },
      },
      maxPoints: 4,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 10,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_CZE") {
    return createJune2025OriginalTask();
  }

  // Aby wykres był ładnie widoczny, trzymamy się przedziału x ∈ [-6, 6]
  const x1Options = [-5, -4];
  const x2Options = [-3, -2];
  const x3Options = [0, 1, 2];
  const x4Options = [3, 4, 5];

  for (let attempt = 0; attempt < 400; attempt++) {
    let x1 = x1Options[randInt(0, x1Options.length - 1)];
    let x2 = x2Options[randInt(0, x2Options.length - 1)];
    let x3 = x3Options[randInt(0, x3Options.length - 1)];
    let x4 = x4Options[randInt(0, x4Options.length - 1)];

    if (!(x1 < x2 && x2 < x3 && x3 < x4)) continue;

    // losowe domknięcia krańców dziedziny
    const domainLeftClosed = randInt(0, 1) === 1;
    const domainRightClosed = randInt(0, 1) === 1;

    // całkowite w (x1, x2) – do wyboru ineqRefX
    const integerCandidates = [];
    for (let k = Math.ceil(x1 + 1e-9); k <= Math.floor(x2 - 1e-9); k++) {
      integerCandidates.push(k);
    }
    if (!integerCandidates.length) continue;

    // LEWY ODCINEK: malejący, przechodzi przez (x2, 0)
    const a1 = -[1, 2][randInt(0, 1)];
    const b1 = -a1 * x2;
    const f1 = (x) => a1 * x + b1;
    const yL = f1(x1);
    if (!(yL > 0 && yL <= 5)) continue;

    // ŚRODKOWY: 0 na (x2, x3]
    const a2 = 0;
    const b2 = 0;
    const f2 = () => 0;

    // Punkt odniesienia do nierówności – w lewym kawałku
    const ineqRefX =
      integerCandidates[randInt(0, integerCandidates.length - 1)];
    const fRef = f1(ineqRefX);
    if (!(fRef > 0 && fRef < yL)) continue;

    // PRAWY ODCINEK: rosnący, cały powyżej fRef
    const a3 = [1, 2][randInt(0, 1)];

    const minYStart = Math.max(Math.ceil(fRef + 1e-9), 1);
    const possibleStarts = [];
    for (let y = minYStart; y <= 4; y++) {
      possibleStarts.push(y);
    }
    if (!possibleStarts.length) continue;

    const yRstart = possibleStarts[randInt(0, possibleStarts.length - 1)];
    const b3 = yRstart - a3 * x3;
    const f3 = (x) => a3 * x + b3;
    const yRend = f3(x4);

    // wymuszamy skok w x3 – prawa gałąź nie może mieć tam wartości 0
    const yAtX3Right = f3(x3);
    const yAtX3Middle = f2(x3); // = 0
    if (Math.abs(yAtX3Right - yAtX3Middle) < 1e-9) continue;

    if (!(yRend > fRef && yRend <= 5)) continue;

    // spójny zbiór wartości: bez dziury między lewym i prawym ramieniem
    if (yRstart > yL) continue;

    // PRZEDZIAŁY – bazowa wersja jak w maturze, ale z losową dziedziną
    const interval1 = {
      left: x1,
      right: x2,
      leftClosed: domainLeftClosed,
      rightClosed: true,
    };
    const interval2 = {
      left: x2,
      right: x3,
      leftClosed: false,
      rightClosed: true,
    };
    const interval3 = {
      left: x3,
      right: x4,
      leftClosed: false,
      rightClosed: domainRightClosed,
    };

    const domainInterval = {
      left: x1,
      right: x4,
      leftClosed: domainLeftClosed,
      rightClosed: domainRightClosed,
    };

    const maxY = Math.max(yL, yRend);
    const rangeInterval = {
      left: 0,
      right: maxY,
      leftClosed: true,
      rightClosed: true,
    };

    const zerosInterval = {
      left: x2,
      right: x3,
      leftClosed: true,
      rightClosed: true,
    };

    // bazowe rozwiązanie f(x) < f(ineqRefX): jeden przedział (ineqRefX, x3]
    const ineqSolution = {
      left: ineqRefX,
      right: x3,
      leftClosed: false,
      rightClosed: true,
    };

    // PUNKTY DO WYKRESU
    const eps = 0;
    const x1Line = x1 + eps;
    const x2LineRight = x2 + eps;
    const x3LineLeft = x3 - eps;
    const x3LineRight = x3 + eps;
    const x4Line = x4 - eps;

    const points = {
      A: {
        x: x1,
        y: yL,
        markerSymbol: domainLeftClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      AL: {
        x: x1Line,
        y: f1(x1Line),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      B: {
        x: x2,
        y: f2(x2),
        showDot: false,
        showLabel: false,
      },
      Cline: {
        x: x2LineRight,
        y: f2(x2LineRight),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      Dline: {
        x: x3LineLeft,
        y: f2(x3LineLeft),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      D: {
        x: x3,
        y: f2(x3),
        markerSymbol: "circle-filled",
        showLabel: false,
      },
      E: {
        x: x3,
        y: f3(x3),
        markerSymbol: "circle-open",
        showLabel: false,
      },
      Eline: {
        x: x3LineRight,
        y: f3(x3LineRight),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      Fline: {
        x: x4Line,
        y: f3(x4Line),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      F: {
        x: x4,
        y: f3(x4),
        markerSymbol: domainRightClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    };

    const baseData = {
      a1,
      b1,
      a2,
      b2,
      a3,
      b3,
      interval1,
      interval2,
      interval3,
      domainInterval,
      rangeInterval,
      zerosInterval,
      ineqRefX,
      ineqSolution,
      points,
      maxPoints: 4,
      ineqRelation: "<",
    };

    // LOSUJEMY WARIANT ODBICIA: 0 – baza, 1 – OY, 2 – OX, 3 – OX+OY
    const variant = randInt(0, 3);
    let finalData;

    if (variant === 0) {
      finalData = baseData;
    } else if (variant === 1) {
      finalData = applyFlipOY(baseData);
    } else if (variant === 2) {
      finalData = applyFlipOX(baseData);
    } else {
      const afterX = applyFlipOX(baseData);
      finalData = applyFlipOY(afterX);
    }

    // losujemy, czy będzie równość w nierówności
    const wantEquality = randInt(0, 1) === 1;

    if (wantEquality) {
      if (finalData.ineqRelation === "<") {
        finalData.ineqRelation = "<=";
      } else if (finalData.ineqRelation === ">") {
        finalData.ineqRelation = ">=";
      }

      if (finalData.ineqRelation === "<=" || finalData.ineqRelation === ">=") {
        const x0 = finalData.ineqRefX;
        const solArray = Array.isArray(finalData.ineqSolution)
          ? finalData.ineqSolution
          : [finalData.ineqSolution];

        solArray.forEach((I) => {
          if (!I) return;
          if (Math.abs(I.left - x0) < TOL) I.leftClosed = true;
          if (Math.abs(I.right - x0) < TOL) I.rightClosed = true;
        });

        finalData.ineqSolution = solArray;
      }
    }

    // LICZYMY ZBIÓR WARTOŚCI PO ODBICIACH
    const { intervals: rangeIntervals, hull: rangeHull } =
    computeRangeFromData(finalData);
    finalData.rangeIntervals = sortIntervalsByLeft(rangeIntervals);
    finalData.rangeInterval = rangeHull;

    // ✅ SYNC DZIEDZINY Z KAWAŁKAMI (żeby domknięcia zawsze zgadzały się z wykresem)
    finalData.domainInterval = {
    left: finalData.interval1.left,
    right: finalData.interval3.right,
    leftClosed: !!finalData.interval1.leftClosed,
    rightClosed: !!finalData.interval3.rightClosed,
  };

  // (opcjonalnie, ale bezpiecznie) dociągnij markery pod dziedzinę:
  if (finalData.points?.A) {
    finalData.points.A.markerSymbol = finalData.domainInterval.leftClosed
      ? "circle-filled"
      : "circle-open";
  }
  if (finalData.points?.F) {
    finalData.points.F.markerSymbol = finalData.domainInterval.rightClosed
      ? "circle-filled"
      : "circle-open";
  }

  // ✅ WYKLUCZAMY "NIEZGRABNE" SKLEJENIE W PUNKCIE x3
  // Chcemy, żeby prawa gałąź była wyraźnie rozłączna z poziomem 0 w punkcie x3.
  const joinX = finalData.interval3.left;   // to samo co finalData.interval2.right
  const yMid = finalData.a2 * joinX + finalData.b2;   // środkowy kawałek (u Ciebie = 0)
  const yRight = finalData.a3 * joinX + finalData.b3; // start prawego kawałka

  // jeśli prawa gałąź startuje "na zerze" (albo bardzo blisko), to odrzucamy losowanie
  if (Math.abs(yRight - yMid) < 1 - 1e-9) {
    continue;
  }

    return {
      type: "MaturaPiecewiseFunctionIntervalsOpenTask",
      data: finalData,
    };
  }

  return null;
}

/* ========================================================================== */
/* RENDER ZADANIA                                                             */
/* ========================================================================== */

export function renderTask({ data }) {
  const {
    a1,
    b1,
    a2,
    b2,
    a3,
    b3,
    interval1,
    interval2,
    interval3,
    ineqRefX,
    points,
    ineqRelation,
  } = data;

  const sizePx = 350;

  const formatIntervalLatex = (I) => {
    if (!I) return "";
    return intervalObjectToLatex(I);
  };

  const piece1Latex = formatLinear(a1, b1);
  const piece2Latex = Math.abs(a2) < 1e-9 ? String(b2) : formatLinear(a2, b2);
  const piece3Latex = formatLinear(a3, b3);

  const i1Latex = formatIntervalLatex(interval1);
  const i2Latex = formatIntervalLatex(interval2);
  const i3Latex = formatIntervalLatex(interval3);

  const relationSymbolLatex =
    ineqRelation === "<"
      ? "<"
      : ineqRelation === ">"
      ? ">"
      : ineqRelation === "<="
      ? "\\le"
      : "\\ge";

  return (
    <div className="flex flex-col gap-3">
      <p>
        Funkcja <InlineMath math={"f"} /> jest określona następująco:
      </p>

      <BlockMath
        math={`f(x)=
\\begin{cases}
${piece1Latex}, & \\text{dla } x\\in ${i1Latex},\\\\
${piece2Latex}, & \\text{dla } x\\in ${i2Latex},\\\\
${piece3Latex}, & \\text{dla } x\\in ${i3Latex}.
\\end{cases}`}
      />

      <div style={{ margin: "0 auto", maxWidth: sizePx }}>
        <MaturaFunctionGraph
          width={sizePx}
          points={points}
          segments={[
            { p1: "AL", p2: "B" },
            { p1: "B", p2: "Dline" },
            { p1: "Eline", p2: "Fline" },
          ]}
          xRange={[-6, 6]}
          yRange={[-6, 6]}
        />
      </div>


      <p>
        Uzupełnij zdania. Wpisz odpowiednie przedziały w wykropkowanych
        miejscach, aby zdania były prawdziwe.
      </p>

      <ol style={{ paddingLeft: 20 }}>
        <li>
          Dziedziną funkcji <InlineMath math={"f"} /> jest przedział{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          Zbiorem wartości funkcji <InlineMath math={"f"} /> jest przedział{" "}
          lub suma przedziałów{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          Miejscami zerowymi funkcji <InlineMath math={"f"} /> są argumenty
          należące do przedziału{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          Zbiorem wszystkich rozwiązań nierówności{" "}
          <InlineMath
            math={`f(x) ${relationSymbolLatex} f(${ineqRefX})`}
          />{" "}
          jest zbiór{" "}
          <InlineMath math={"\\_\\_\\_\\_"} /> (może być jednym przedziałem
          lub sumą przedziałów).
        </li>
      </ol>
    </div>
  );
}

/* ========================================================================== */
/* INPUT UŻYTKOWNIKA                                                          */
/* ========================================================================== */

export function renderInput(task, userInput = {}, onChange) {
  // UniversalQuizRunner przekazuje tu "task" bezpośrednio:
  // renderInput(task, userAnswers[i], onChange, false)
  const {
    data: { ineqRefX, ineqRelation },
  } = task;

  const relationSymbolLatex =
    ineqRelation === "<"
      ? "<"
      : ineqRelation === ">"
      ? ">"
      : ineqRelation === "<="
      ? "\\le"
      : "\\ge";

  // userInput na początku jest stringiem "", później obiektem – trzeba to bezpiecznie ogarnąć
  const ui =
    userInput && typeof userInput === "object" ? userInput : {};

  const handleChange = (key) => (value) => {
    const base =
      userInput && typeof userInput === "object" ? userInput : {};
    onChange({
      ...base,
      [key]: value,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <OpenTaskScoringHint variant="independent" />
      <div>
        <strong>1.</strong> Dziedzina:{" "}
        <IntervalInput
          value={ui.D ?? ""}
          onChange={handleChange("D")}
          allowUnion={false}
        />
      </div>
      <div>
        <strong>2.</strong> Zbiór wartości:{" "}
        <IntervalInput
          value={ui.R ?? ""}
          onChange={handleChange("R")}
          allowUnion={true}
        />
      </div>
      <div>
        <strong>3.</strong> Miejsca zerowe:{" "}
        <IntervalInput
          value={ui.Z ?? ""}
          onChange={handleChange("Z")}
          allowUnion={false}
        />
      </div>
      <div>
        <strong>4.</strong> Zbiór rozwiązań nierówności{" "}
        <InlineMath
          math={`f(x) ${relationSymbolLatex} f(${ineqRefX})`}
        />{" "}
        (może być sumą przedziałów):{" "}
        <IntervalInput
          value={ui.S ?? ""}
          onChange={handleChange("S")}
          allowUnion={true}
        />
      </div>
    </div>
  );
}


/* ========================================================================== */
/* SPRAWDZANIE ODPOWIEDZI                                                     */
/* ========================================================================== */

export function checkAnswer(task, userInput = {}) {
  const {
    data: {
      domainInterval,
      rangeInterval,
      rangeIntervals,
      zerosInterval,
      ineqSolution,
      maxPoints,
    },
  } = task;

  let score = 0;
  const parts = {};

  const sameInterval = (A, B) =>
    A &&
    B &&
    A.left === B.left &&
    A.right === B.right &&
    !!A.leftClosed === !!B.leftClosed &&
    !!A.rightClosed === !!B.rightClosed;

  // 1. Dziedzina
  const rawD = getIntervalRaw(userInput, "D");
  const parsedD = rawD && rawD.trim() ? parseSingleInterval(rawD) : null;
  const ok1 = parsedD && sameInterval(parsedD, domainInterval);
  if (ok1) score++;
  parts.domain = ok1;

  // 2. Zbiór wartości – suma przedziałów
  const rawR = getIntervalRaw(userInput, "R");
  const parsedRIntervals =
    rawR && rawR.trim() ? parseIntervalsUnion(rawR) : null;

  const expectedRangeIntervals =
    rangeIntervals && rangeIntervals.length
      ? rangeIntervals
      : rangeInterval
      ? [rangeInterval]
      : [];

  const ok2 =
    parsedRIntervals &&
    expectedRangeIntervals.length > 0 &&
    intervalsEqualList(expectedRangeIntervals, parsedRIntervals);

  if (ok2) score++;
  parts.range = ok2;

  // 3. Miejsca zerowe
  const rawZ = getIntervalRaw(userInput, "Z");
  const parsedZ = rawZ && rawZ.trim() ? parseSingleInterval(rawZ) : null;
  const ok3 = parsedZ && sameInterval(parsedZ, zerosInterval);
  if (ok3) score++;
  parts.zeros = ok3;

  // 4. Nierówność – suma przedziałów
  const rawS = getIntervalRaw(userInput, "S");
  const parsedSIntervals =
    rawS && rawS.trim() ? parseIntervalsUnion(rawS) : null;

  const expectedIntervals = Array.isArray(ineqSolution)
    ? ineqSolution
    : ineqSolution
    ? [ineqSolution]
    : [];

  const ok4 =
    parsedSIntervals &&
    expectedIntervals.length > 0 &&
    intervalsEqualList(expectedIntervals, parsedSIntervals);

  if (ok4) score++;
  parts.ineq = ok4;

  const invalidOrderLabels = [];
  if (rawD && intervalHasInvalidOrder(rawD)) invalidOrderLabels.push("dziedzina");
  if (rawR && intervalHasInvalidOrder(rawR)) invalidOrderLabels.push("zbiór wartości");
  if (rawZ && intervalHasInvalidOrder(rawZ)) invalidOrderLabels.push("miejsca zerowe");
  if (rawS && intervalHasInvalidOrder(rawS))
    invalidOrderLabels.push("zbiór rozwiązań nierówności");

  const message =
    invalidOrderLabels.length > 0
      ? `Uwaga: w polu/polach ${invalidOrderLabels.join(
          ", "
        )} przedział zapisano od większej do mniejszej liczby. Zapisuj przedziały od mniejszej do większej (lewy koniec ≤ prawy koniec).`
      : null;

  return {
    score,
    maxScore: maxPoints,
    parts,
    message,
    messageType: message ? "hint" : null,
  };
}

/* ========================================================================== */
/* RENDER POPRAWNEJ ODPOWIEDZI                                                */
/* ========================================================================== */

export function renderAnswer({ data }) {
  const {
    domainInterval,
    rangeInterval,
    rangeIntervals,
    zerosInterval,
    ineqSolution,
  } = data;

  const D = intervalObjectToLatex(domainInterval);

  const rangeInts =
    rangeIntervals && rangeIntervals.length
      ? sortIntervalsByLeft(rangeIntervals)
      : rangeInterval
      ? [rangeInterval]
      : [];
  const R = intervalsToLatexUnion(rangeInts);

  const Z = intervalObjectToLatex(zerosInterval);

  const expectedIntervals = Array.isArray(ineqSolution)
    ? sortIntervalsByLeft(ineqSolution)
    : ineqSolution
    ? [ineqSolution]
    : [];
  const S = intervalsToLatexUnion(expectedIntervals);

  return (
    <div className="flex flex-col gap-1" style={{ textAlign: "left" }}>
      <div>
        <strong>1.</strong> Dziedzina: <InlineMath math={D} />
      </div>
      <div>
        <strong>2.</strong> Zbiór wartości: <InlineMath math={R} />
      </div>
      <div>
        <strong>3.</strong> Miejsca zerowe: <InlineMath math={Z} />
      </div>
      <div>
        <strong>4.</strong> Rozwiązania nierówności:{" "}
        <InlineMath math={S} />
      </div>
    </div>
  );
}

/* ========================================================================== */
/* RENDER ODPOWIEDZI UŻYTKOWNIKA                                              */
/* ========================================================================== */

export function renderUserAnswer(userInput = {}) {
  // UniversalQuizRunner wywołuje: renderUserAnswer(userAnswers[i])
  const ui =
    userInput && typeof userInput === "object" ? userInput : {};

  const Dval = ui?.D;
  const Rval = ui?.R;
  const Zval = ui?.Z;
  const Sval = ui?.S;

  const getParsedIntervalsFromValue = (value) => {
    if (!value || typeof value !== "object") return null;
    if (value.kind === "intervals" && Array.isArray(value.intervals)) {
      return value.intervals;
    }
    if (
      value.parsed &&
      value.parsed.kind === "intervals" &&
      Array.isArray(value.parsed.intervals)
    ) {
      return value.parsed.intervals;
    }
    return null;
  };

  // Fallback do podglądu "surowego" przedziału w stylu matematycznym,
  // nawet gdy parser odrzuci format (np. odwrócona kolejność końców).
  const rawIntervalToLatex = (raw) =>
    String(raw || "")
      .trim()
      .replace(/[−–—]/g, "-")
      .replace(/;/g, ",")
      .replace(/\s*(?:∪|U)\s*/g, " \\\\cup ");

  const renderSingleIntervalFromRaw = (raw) => {
    if (!raw || !raw.trim()) {
      return <em>(brak odpowiedzi)</em>;
    }
    const I = parseSingleInterval(raw);
    if (!I) {
      return <InlineMath math={rawIntervalToLatex(raw)} />;
    }
    return <InlineMath math={intervalObjectToLatex(I)} />;
  };

  const renderUnionFromRaw = (raw) => {
    if (!raw || !raw.trim()) {
      return <em>(brak odpowiedzi)</em>;
    }
    const arr = parseIntervalsUnion(raw);
    if (!arr || !arr.length) {
      return <InlineMath math={rawIntervalToLatex(raw)} />;
    }
    return <InlineMath math={intervalsToLatexUnion(arr)} />;
  };

  const renderSingleIntervalFromAny = (value, key) => {
    const parsedIntervals = getParsedIntervalsFromValue(value);
    if (parsedIntervals && parsedIntervals.length === 1) {
      return <InlineMath math={intervalObjectToLatex(parsedIntervals[0])} />;
    }
    if (parsedIntervals && parsedIntervals.length > 1) {
      return <InlineMath math={intervalsToLatexUnion(parsedIntervals)} />;
    }
    return renderSingleIntervalFromRaw(getIntervalRaw(ui, key));
  };

  const renderUnionFromAny = (value, key) => {
    const parsedIntervals = getParsedIntervalsFromValue(value);
    if (parsedIntervals && parsedIntervals.length > 0) {
      return <InlineMath math={intervalsToLatexUnion(parsedIntervals)} />;
    }
    return renderUnionFromRaw(getIntervalRaw(ui, key));
  };

  return (
    <div className="flex flex-col gap-1" style={{ textAlign: "left" }}>
      <div>
        <strong>1.</strong> Dziedzina: {renderSingleIntervalFromAny(Dval, "D")}
      </div>
      <div>
        <strong>2.</strong> Zbiór wartości: {renderUnionFromAny(Rval, "R")}
      </div>
      <div>
        <strong>3.</strong> Miejsca zerowe: {renderSingleIntervalFromAny(Zval, "Z")}
      </div>
      <div>
        <strong>4.</strong> Zbiór rozwiązań nierówności:{" "}
        {renderUnionFromAny(Sval, "S")}
      </div>
    </div>
  );
}


/* ========================================================================== */
/* WYJAŚNIENIE                                                                */
/* ========================================================================== */

export function renderExplanation({ data }) {
  const {
    a1,
    b1,
    a2,
    b2,
    a3,
    b3,
    interval1,
    interval2,
    interval3,
    domainInterval,
    rangeInterval,
    rangeIntervals,
    zerosInterval,
    ineqRefX,
    ineqSolution,
    ineqRelation,
    points,
  } = data;

  // Ładniejszy zapis stałej gałęzi (bez "0x")
  const piece1Latex = formatLinear(a1, b1);
  const piece2Latex = Math.abs(a2) < 1e-9 ? String(b2) : formatLinear(a2, b2);
  const piece3Latex = formatLinear(a3, b3);

  const funcLatex = `
f(x)=
\\begin{cases}
${piece1Latex}, & \\text{dla } x\\in ${intervalObjectToLatex(interval1)},\\\\
${piece2Latex}, & \\text{dla } x\\in ${intervalObjectToLatex(interval2)},\\\\
${piece3Latex}, & \\text{dla } x\\in ${intervalObjectToLatex(interval3)}.
\\end{cases}
`.trim();

  const rangeInts =
    rangeIntervals && rangeIntervals.length
      ? sortIntervalsByLeft(rangeIntervals)
      : rangeInterval
      ? [rangeInterval]
      : [];

  const expectedIntervals = Array.isArray(ineqSolution)
    ? sortIntervalsByLeft(ineqSolution)
    : ineqSolution
    ? [ineqSolution]
    : [];

  const relation = ineqRelation || "<";
  const relationSymbolLatex =
    relation === "<"
      ? "<"
      : relation === ">"
      ? ">"
      : relation === "<="
      ? "\\le"
      : "\\ge";

  const fRefValue = valueOfF(data, ineqRefX);

  return (
    <div className="flex flex-col gap-2" style={{ textAlign: "left" }}>
      <p>Funkcja jest określona wzorem odcinkowym:</p>
      <BlockMath math={funcLatex} />

      {/* 1) DZIEDZINA */}
      <p>
        <strong>Krok 1. Dziedzina.</strong> Dziedzina to wszystkie argumenty{" "}
        <InlineMath math={"x"} />, dla których funkcja jest określona
        (czyli cała „rozpiętość” wykresu na osi <InlineMath math={"OX"} />).
      </p>
      <BlockMath math={`D_f = ${intervalObjectToLatex(domainInterval)}`} />

      {/* 2) ZBIÓR WARTOŚCI */}
      <p>
        <strong>Krok 2. Zbiór wartości.</strong> Zbiór wartości to wszystkie
        wartości <InlineMath math={"y"} />, które przyjmuje funkcja. Odczytujemy
        go z wykresu jako przedział lub sumę przedziałów na osi <InlineMath math={"OY"} />.
      </p>
      <BlockMath
        math={`\\operatorname{Wart}(f)= ${intervalsToLatexUnion(rangeInts)}`}
      />

      {/* 3) MIEJSCA ZEROWE */}
      <p>
        <strong>Krok 3. Miejsca zerowe.</strong> Miejsca zerowe to te{" "}
        <InlineMath math={"x"} />, dla których <InlineMath math={"f(x)=0"} />,
        czyli gdzie wykres leży na osi <InlineMath math={"OX"} />.
      </p>
      <BlockMath math={`Z_f = ${intervalObjectToLatex(zerosInterval)}`} />

      {/* 4) NIERÓWNOŚĆ */}
      <p>
        <strong>Krok 4. Nierówność.</strong> Najpierw wyznaczamy wartość{" "}
        <InlineMath math={`f(${ineqRefX})`} />, a potem zaznaczamy na wykresie
        te fragmenty, dla których{" "}
        <InlineMath math={`f(x) ${relationSymbolLatex} f(${ineqRefX})`} />.
      </p>

      <BlockMath math={`f(${ineqRefX}) = ${String(fRefValue)}`} />

      <MaturaPiecewiseInequalityExplanationGraph
        points={points}
        segments={[
          { p1: "AL", p2: "B" },
          { p1: "B", p2: "Dline" },
          { p1: "Eline", p2: "Fline" },
        ]}
        solutionIntervals={expectedIntervals}
        threshold={fRefValue}
        operator={relation}
      />

      <p>
        Odczytujemy z osi <InlineMath math={"OX"} /> przedziały argumentów i
        uwzględniamy domknięcia (kółka pełne / puste). Otrzymujemy:
      </p>
      <BlockMath math={`S = ${intervalsToLatexUnion(expectedIntervals)}`} />
    </div>
  );
}

